"use client"

import { useEffect } from "react"
import { useSearchParams, usePathname, useRouter } from "next/navigation"
import { toast } from "sonner"
import { DINING_DISABLED_QUERY_KEY } from "@/lib/feature-flags"

/**
 * Shows a Sonner toast when the dining kill-switch redirected the user.
 *
 * The middleware (`proxy.ts`) adds `?dining_off=1` to the redirect target
 * when `NEXT_PUBLIC_ENABLE_DINING=false`. This component reads that param
 * client-side, fires the toast, and then scrubs the param from the URL so
 * a navigation back doesn't refire it.
 *
 * Mounted once globally in `app/layout.tsx`.
 */
export function DiningKillSwitchToast() {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()

  useEffect(() => {
    if (searchParams.get(DINING_DISABLED_QUERY_KEY) !== "1") return

    toast.warning("Dining service is temporarily unavailable", {
      description: "Ask a manager if you need to file an order on paper.",
      duration: 8000,
    })

    // Strip the param so refreshing the page doesn't re-toast.
    const next = new URLSearchParams(searchParams.toString())
    next.delete(DINING_DISABLED_QUERY_KEY)
    const qs = next.toString()
    router.replace(qs ? `${pathname}?${qs}` : pathname)
  }, [searchParams, pathname, router])

  return null
}
